import React, { useEffect, useState } from 'react';
import { Receipt, Sparkles, ChevronDown, Loader2, ExternalLink, TrendingDown, TrendingUp } from 'lucide-react';
import { apiFetch } from '../App';

/**
 * Billing + Credit History sections for the Settings page.
 *
 * Two side-by-side cards (or stacked on mobile):
 *   • Billing — the user's own PayPal purchases (from payment_transactions)
 *   • Credits — a unified timeline reconstructed from purchases, gifts,
 *               AI Studio generations, and video renders.
 *
 * Both endpoints are user-scoped on the backend — a user only ever sees
 * their own history, regardless of admin status.
 */

const _fmtMoney = (amount, currency = 'USD') => {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(Number(amount));
  } catch (_) {
    return `${amount} ${currency}`;
  }
};

const _fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return String(iso);
  }
};

const _statusBadge = (status, paymentStatus) => {
  const s = (paymentStatus || status || '').toLowerCase();
  if (s === 'paid' || s === 'completed') return { label: 'Paid', color: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' };
  if (s === 'pending' || s === 'unpaid') return { label: 'Pending', color: 'bg-amber-500/15 text-amber-300 border-amber-500/30' };
  if (s === 'failed' || s === 'cancelled' || s === 'declined') return { label: 'Failed', color: 'bg-rose-500/15 text-rose-300 border-rose-500/30' };
  return { label: s || 'Unknown', color: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30' };
};

const BillingHistorySection = () => {
  const [txns, setTxns] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(true);
  const [showAllTxns, setShowAllTxns] = useState(false);

  const [entries, setEntries] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [showAllCredits, setShowAllCredits] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiFetch('/transactions/me?limit=50');
        if (!cancelled) setTxns(data?.transactions || []);
      } catch (_) {
        // Silent — the section just shows empty state.
      } finally {
        if (!cancelled) setLoadingTxns(false);
      }
      try {
        const { data } = await apiFetch('/credits/history?limit=100');
        if (!cancelled) {
          setEntries(data?.entries || []);
          setBalance(data?.current_balance ?? null);
        }
      } catch (_) {
        // Silent.
      } finally {
        if (!cancelled) setLoadingCredits(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visibleTxns = showAllTxns ? txns : txns.slice(0, 5);
  const visibleEntries = showAllCredits ? entries : entries.slice(0, 8);

  return (
    <>
      {/* Billing (purchases) */}
      <div className="glass-card p-6 mb-6" data-testid="settings-billing-section">
        <h2 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-primary" />
          Billing History
        </h2>
        <p className="text-sm text-zinc-400 mb-4">
          Your PayPal purchases. Each row shows the amount, credits added, and status.
        </p>

        {loadingTxns ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading your history…
          </div>
        ) : visibleTxns.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-4" data-testid="billing-empty">
            No purchases yet. When you buy credits, your receipts will show up here.
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="billing-list">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-zinc-500 border-b border-white/5">
                <tr>
                  <th className="text-left py-2 pr-4 font-medium">Date</th>
                  <th className="text-left py-2 pr-4 font-medium">Package</th>
                  <th className="text-right py-2 pr-4 font-medium">Amount</th>
                  <th className="text-right py-2 pr-4 font-medium">Credits</th>
                  <th className="text-left py-2 pr-4 font-medium">Status</th>
                  <th className="text-left py-2 font-medium">PayPal ID</th>
                </tr>
              </thead>
              <tbody className="text-zinc-200">
                {visibleTxns.map((t) => {
                  const badge = _statusBadge(t.status, t.payment_status);
                  return (
                    <tr
                      key={t.transaction_id}
                      className="border-b border-white/5 last:border-b-0"
                      data-testid={`billing-row-${t.transaction_id}`}
                    >
                      <td className="py-2 pr-4 whitespace-nowrap text-zinc-400">
                        {_fmtDate(t.completed_at || t.created_at)}
                      </td>
                      <td className="py-2 pr-4">{t.package_name || '—'}</td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap">
                        {_fmtMoney(t.amount, t.currency)}
                      </td>
                      <td className="py-2 pr-4 text-right whitespace-nowrap text-primary font-medium">
                        {t.credits ? `+${t.credits}` : '—'}
                      </td>
                      <td className="py-2 pr-4">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${badge.color}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-2 font-mono text-xs text-zinc-500">
                        {t.session_id ? (
                          <span title={t.session_id}>{t.session_id.slice(0, 12)}…</span>
                        ) : (
                          <span className="text-zinc-700">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {txns.length > 5 && (
              <button
                type="button"
                onClick={() => setShowAllTxns((v) => !v)}
                className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                data-testid="billing-toggle-all"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAllTxns ? 'rotate-180' : ''}`} />
                {showAllTxns ? 'Show recent 5' : `Show all ${txns.length}`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Credits history */}
      <div className="glass-card p-6 mb-6" data-testid="settings-credits-section">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Credits History
          </h2>
          {balance !== null && (
            <div className="text-right shrink-0" data-testid="credits-current-balance">
              <div className="text-xs uppercase tracking-wide text-zinc-500">Current balance</div>
              <div className="text-2xl font-semibold text-primary">{balance.toLocaleString()}</div>
            </div>
          )}
        </div>
        <p className="text-sm text-zinc-400 mb-4">
          Every credit you&apos;ve gained and spent — purchases, gifts, AI clip generations, video renders.
        </p>

        {loadingCredits ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Building your timeline…
          </div>
        ) : visibleEntries.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-4" data-testid="credits-empty">
            No credit activity yet. Once you buy credits or use them, everything will appear here.
          </div>
        ) : (
          <div className="space-y-1" data-testid="credits-timeline">
            {visibleEntries.map((e) => {
              const isIn = (e.delta || 0) > 0;
              return (
                <div
                  key={e.id}
                  className="flex items-center gap-3 py-2 px-2 rounded-md hover:bg-white/5 transition-colors"
                  data-testid={`credits-row-${e.id}`}
                >
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${isIn ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}>
                    {isIn ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-200 truncate">{e.label}</div>
                    <div className="text-xs text-zinc-500">
                      {_fmtDate(e.ts)}
                      {e.amount_usd ? <> · <span className="font-mono">{_fmtMoney(e.amount_usd)}</span></> : null}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-semibold text-sm ${isIn ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {isIn ? '+' : ''}{(e.delta || 0).toLocaleString()}
                    </div>
                    <div className="text-xs text-zinc-500">Bal: {(e.balance_after || 0).toLocaleString()}</div>
                  </div>
                </div>
              );
            })}

            {entries.length > 8 && (
              <button
                type="button"
                onClick={() => setShowAllCredits((v) => !v)}
                className="mt-3 inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                data-testid="credits-toggle-all"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showAllCredits ? 'rotate-180' : ''}`} />
                {showAllCredits ? 'Show recent 8' : `Show all ${entries.length}`}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BillingHistorySection;
